import { MessagePrimitive, ThreadPrimitive } from "@assistant-ui/react";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "../../button/Button";
import { ThinkingIndicator } from "./parts/ThinkingIndicator";
import { ToolUse } from "./tools/ToolUse/ToolUse";
import { TextMessage } from "./parts/TextMessage";
import { UserQuestionMessage } from "./parts/UserQuestionMessage";
import styles from "./AiChatThread.scss";

const msgKey = "aiChatMessages";

// Part routing for assistant messages, matching the previous bespoke
// behaviour: text renders markdown, tool calls render our tool cards, and
// reasoning + step lifecycle parts are intentionally not painted (the waiting
// indicator covers the "thinking" phase). An empty element — not null — is
// returned for hidden parts so assistant-ui never falls back to its own
// default part renderer.
const renderPart = ({ part }) => {
    if (part.type === "text") {
        return <TextMessage text={part.text} />;
    }
    if (part.type === "tool-call") {
        return <ToolUse part={part} />;
    }
    return <React.Fragment />;
};

/**
 * Chat thread in the recommended assistant-ui shape:
 * ThreadPrimitive.Root (flex column) → ThreadPrimitive.Viewport (the thread's
 * own scroll region) → messages → ThreadPrimitive.ViewportFooter (composer).
 *
 * turnAnchor="top" selects the library's ChatGPT-style turn behaviour (the
 * freshly sent prompt anchors near the top of the viewport and the answer
 * streams beneath it). autoScroll is passed explicitly because the primitive
 * defaults it to false in top-anchor mode; with it on, bottom-following
 * engages whenever the user is at the bottom and releases on scroll-up — all
 * owned by the primitives, no custom scroll code. ScrollToBottom is the
 * library's own control and self-hides while at bottom.
 */
export const AiChatThread = ({ composer, children }) => {
    const intl = useIntl();
    return (
        <ThreadPrimitive.Root className={styles.thread}>
            <ThreadPrimitive.Viewport turnAnchor="top" autoScroll className={styles.viewport} data-thread-viewport="">
                <div className={styles.content}>
                    <ThreadPrimitive.Messages>
                        {({ message }) =>
                            message.role === "user" ? (
                                <MessagePrimitive.Root key={message.id} className={styles.message}>
                                    <UserQuestionMessage />
                                </MessagePrimitive.Root>
                            ) : (
                                <MessagePrimitive.Root key={message.id} className={styles.message}>
                                    <MessagePrimitive.Parts>{renderPart}</MessagePrimitive.Parts>
                                </MessagePrimitive.Root>
                            )
                        }
                    </ThreadPrimitive.Messages>
                    {children}
                    <ThreadPrimitive.If empty>
                        <div>
                            <FormattedMessage id={`${msgKey}.emptyState`} />
                        </div>
                    </ThreadPrimitive.If>
                    <ThinkingIndicator />
                </div>
                <ThreadPrimitive.ScrollToBottom
                    className={styles.scrollToBottom}
                    render={
                        <Button
                            color="silent"
                            circle
                            size="small"
                            icon="arrow-down"
                            title={intl.formatMessage({ id: `${msgKey}.scrollToBottom` })}
                        />
                    }
                />
            </ThreadPrimitive.Viewport>
            <ThreadPrimitive.ViewportFooter className={styles.footer}>
                <div className={styles.footerContent}>{composer}</div>
            </ThreadPrimitive.ViewportFooter>
        </ThreadPrimitive.Root>
    );
};
